import { useCallback, useEffect, useMemo, useState, type RefObject } from "react";
import { shallowEqual } from "react-redux";
import { showAemInsuranceRates } from "../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { type CarConfigDispatchType, useCarConfigSelector } from "../redux/store";
import { showCarConfigMonthlyRateSelector } from "../utils/selectorUtils";
import useAphModal from "./useAphModal";
import { setFinanceOption } from "../../../../common-deprecated/redux/actions/UtilActions";
import { MODAL_FINANCE_CALCULATOR, MODAL_INSURANCE_CALCULATOR } from "../utils/modalConstants";
import {
    getFinanceQuoteIdFromStorage,
    getInsuranceQuoteIdFromStorage,
    getSessionStorageParams,
} from "../utils/sessionStorage";
import { isRTL } from "../../../../common-deprecated/settings/utils/commonSettingUtils";
import { useWindowDimensionsContext } from "../../../../common-deprecated/containers/WindowDimensionsProvider";
import type { SummaryTabType } from "../utils/types/SummaryType";
import { useDispatch } from "../../../../shared-logic/redux/store";

type AphSummaryTabsPropType = {
    openTab?: SummaryTabType;
    setOpenTab?: (newTab: SummaryTabType) => void;
    tabVariants?: Record<string, SummaryTabType>;
    tabContainer?: RefObject<HTMLDivElement>;
    tabList?: RefObject<HTMLUListElement>;
};

type AphSummaryTabsReturnType = {
    tabs: SummaryTabType[];
    onTabSwitch: (tab: SummaryTabType) => void;
    onNextTab: () => void;
    isNextTabEnabled: boolean;
    onPreviousTab: () => void;
    isPreviousTabEnabled: boolean;

    canScrollLeft: boolean;
    canScrollRight: boolean;
    canScroll: boolean;
    onScroll: () => void;
};

const defaultTabOrder: SummaryTabType[] = ["overview", "cash", "finance", "insurance", "business"];

const useAphSummaryTabs = ({
    openTab = "overview",
    setOpenTab,
    tabContainer,
    tabList,
    tabVariants,
}: AphSummaryTabsPropType): AphSummaryTabsReturnType => {
    const dispatch = useDispatch<CarConfigDispatchType>();
    const windowWidth = useWindowDimensionsContext().width;
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const sessionStorageParams = useCarConfigSelector(getSessionStorageParams);
    const { internalCode: modelCode, id: modelId } = useCarConfigSelector((state) => state.car.model);
    const tabOrder = useCarConfigSelector((state) => state.carConfigSettings.summaryConfig?.order || defaultTabOrder);
    const showMonthlyRates = useCarConfigSelector(showCarConfigMonthlyRateSelector, shallowEqual);
    const financeOption = useCarConfigSelector((state) => state.utils.financeOption);
    const showInsurance = showAemInsuranceRates(commonSettings, AemFmComponent.Carconfig, "new", modelCode);
    const { showModalHelper } = useAphModal();
    const showBusinessLeasing = useCarConfigSelector((state) => !!state.carConfigSettings.businessLeasing);
    const RTL = isRTL(commonSettings);

    const [canScroll, setCanScroll] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const filterArray = useCallback(
        (item: SummaryTabType) => {
            switch (item) {
                case "overview":
                case "cash":
                    return true;
                case "finance":
                    return showMonthlyRates;
                case "insurance":
                    return showInsurance;
                case "business":
                    return showBusinessLeasing;
                default:
                    return false;
            }
        },
        [showMonthlyRates, showInsurance, showBusinessLeasing],
    );

    const tabs = useMemo(() => {
        return tabOrder.filter(filterArray);
    }, [filterArray]);

    useEffect(() => {
        if (financeOption === "monthly" && showMonthlyRates && openTab === "cash") {
            onTabSwitch("finance");
        }
        if (financeOption === "cash" && openTab === "finance") {
            onTabSwitch("cash");
        }
        // No exhaustive dependencies because we only want to trigger this when financeOption changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [financeOption]);

    const scrollToActiveTab = useCallback(
        (tab: SummaryTabType) => {
            if (!tabList?.current || !tabVariants) return;

            const activeTab = tabList.current.querySelector<HTMLButtonElement>(`#tab-${tabVariants[tab]}`);

            if (!activeTab) return;

            // Scroll in the tabbar
            const tabListRect = tabList.current.getBoundingClientRect();
            const activeTabRect = activeTab.getBoundingClientRect();
            if (activeTabRect.left < tabListRect.left) {
                tabList.current.scrollLeft += activeTabRect.left - tabListRect.left - 16; // 16px buffer to account for paddings
            } else if (activeTabRect.right > tabListRect.right) {
                tabList.current.scrollLeft += activeTabRect.right - tabListRect.right + 16; // 16px buffer to account for paddings
            }
        },
        [tabList?.current],
    );

    const onTabSwitch = useCallback(
        (tab: SummaryTabType = "overview"): void => {
            const tabActions: Partial<Record<SummaryTabType, () => void>> = {
                cash: () => {
                    dispatch(setFinanceOption("cash"));
                },
                finance: () => {
                    // Check if financeQuote has been filled in
                    const financeQuoteId = getFinanceQuoteIdFromStorage(sessionStorageParams, modelId);

                    // If so. Just switch to monthly
                    if (financeQuoteId) {
                        dispatch(setFinanceOption("monthly"));
                    } else if (financeOption === "cash") {
                        // If not and the finance option is cash. Open Calculator
                        showModalHelper(MODAL_FINANCE_CALCULATOR, {});
                    }
                },
                insurance: () => {
                    const insuranceQuoteId = getInsuranceQuoteIdFromStorage(sessionStorageParams, modelId);
                    if (!insuranceQuoteId) {
                        showModalHelper(MODAL_INSURANCE_CALCULATOR, {});
                    }
                },
            };
            tabActions[tab]?.();
            scrollToActiveTab(tab);
            setOpenTab?.(tab);
        },
        [dispatch, modelId, sessionStorageParams, showModalHelper, setOpenTab],
    );

    const checkScrollability = useCallback(() => {
        if (tabList?.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tabList.current;
            if (RTL) {
                setCanScrollLeft(Math.abs(scrollLeft) < scrollWidth - clientWidth - 1);
                setCanScrollRight(scrollLeft < 0);
            } else {
                setCanScrollLeft(scrollLeft > 0);
                setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
            }
        }
    }, [RTL]);

    useEffect(() => {
        if (tabContainer?.current && tabList?.current) {
            const { width: tabContainerWidth } = tabContainer.current.getBoundingClientRect();
            const tabListItems = tabList.current.querySelectorAll("li");
            let tabListWidth = 0;

            if (tabListItems && tabListItems.length) {
                tabListItems.forEach((item) => {
                    tabListWidth += item.offsetWidth;
                });
            }
            setCanScroll(tabListWidth > tabContainerWidth);
            checkScrollability();
        }
    }, [tabContainer, tabList, windowWidth, checkScrollability]);

    const { onNextTab, onPreviousTab, isNextTabEnabled, isPreviousTabEnabled } = useMemo(() => {
        return {
            isNextTabEnabled: openTab ? tabs.indexOf(openTab) < tabs.length - 1 : true,
            isPreviousTabEnabled: openTab ? tabs.indexOf(openTab) > 0 : false,
            onNextTab: () => {
                if (openTab) {
                    onTabSwitch(tabs[tabs.indexOf(openTab) + 1]);
                }
            },
            onPreviousTab: () => {
                if (openTab) {
                    onTabSwitch(tabs[tabs.indexOf(openTab) - 1]);
                }
            },
        };
    }, [openTab, tabs, onTabSwitch]);

    const onScroll = useCallback(() => {
        checkScrollability();
    }, [checkScrollability]);

    return {
        tabs,
        onNextTab,
        onPreviousTab,
        isNextTabEnabled,
        isPreviousTabEnabled,
        onTabSwitch,
        canScroll,
        canScrollLeft,
        canScrollRight,
        onScroll,
    };
};

export default useAphSummaryTabs;
export type SummaryTabPropsTypes = {
    tabId?: string;
    tab?: string;
};
